/**
 * Bounded diagnostics for Player AI permission checks (Task 7).
 *
 * The point of narrowing the triggers is that there should be FEWER checks, and
 * "fewer" is not something a reader can see. This records which trigger asked
 * and when, so a browser session can answer two questions the old tenant-wide
 * audit subscription made unanswerable: did unrelated activity cause a check,
 * and did a relevant access change reach this rail.
 *
 * It holds identifiers and timestamps ONLY — never draft text, never a file
 * name, never a conversation's content — and it is capped, so a long session
 * cannot grow it without bound.
 */

/** Every reason the rail may ask for a fresh authority read. */
export type PermissionRefreshTrigger =
  /** A scoped access-change signal from the workspace that owns the resource. */
  | "access_change"
  | "manual_retry"
  /** The live stream came back after dropping; anything could have been missed. */
  | "reconnect"
  /** The window regained focus after being away long enough to be stale. */
  | "return_after_away"
  /** The window regained focus, but within the freshness window — suppressed. */
  | "return_fresh_skipped";

export interface PermissionRefreshRecord {
  readonly trigger: PermissionRefreshTrigger;
  readonly at: number;
}

const LIMIT = 50;

let records: PermissionRefreshRecord[] = [];
const counts = new Map<PermissionRefreshTrigger, number>();

export function recordPermissionRefresh(
  trigger: PermissionRefreshTrigger,
  at: number = Date.now(),
): void {
  counts.set(trigger, (counts.get(trigger) ?? 0) + 1);
  records = [...records.slice(-(LIMIT - 1)), { trigger, at }];
}

/** The bounded history, oldest first. */
export function permissionRefreshRecords(): readonly PermissionRefreshRecord[] {
  return records;
}

/** How many times each trigger has fired, including the suppressed one. */
export function permissionRefreshCounts(): Readonly<
  Record<PermissionRefreshTrigger, number>
> {
  return {
    manual_retry: counts.get("manual_retry") ?? 0,
    access_change: counts.get("access_change") ?? 0,
    reconnect: counts.get("reconnect") ?? 0,
    return_after_away: counts.get("return_after_away") ?? 0,
    return_fresh_skipped: counts.get("return_fresh_skipped") ?? 0,
  };
}

/** For tests; the log is module state and would otherwise leak between them. */
export function resetPermissionRefreshLog(): void {
  records = [];
  counts.clear();
}
